import { Link } from "react-router-dom";
import { Users, Heart, Sparkles, Users2, UserCheck, Route, ArrowRight, Check } from "lucide-react";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { useLead } from "../lib/store";
import { track } from "../lib/track";

const HERO_IMG =
  "https://images.unsplash.com/photo-1610375229632-c7158c35a537?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1800";

const TYPES = [
  { icon: Users, title: "Family Trip", desc: "Liburan keluarga dengan tempo santai, hotel nyaman, dan aktivitas untuk semua usia." },
  { icon: Heart, title: "Couple", desc: "Escape berdua dengan rute romantis dan momen-momen berkesan." },
  { icon: Sparkles, title: "Honeymoon", desc: "Perjalanan awal menuju selamanya: destinasi romantis, dinner spesial, dan sorotan pribadi." },
  { icon: Users2, title: "Friends", desc: "Trip bareng sahabat dengan itinerary seru dan fleksibel." },
  { icon: UserCheck, title: "Small Group", desc: "Group kecil 6-12 orang dengan layanan private yang tetap eksklusif." },
  { icon: Route, title: "Custom Trip", desc: "Punya rute imajiner sendiri? Kami wujudkan dari nol bersama tim lokal kami." },
];

const BENEFITS = [
  "Itinerary pribadi — bukan ikutan jadwal rombongan",
  "Private transportation dengan driver sendiri",
  "Pilihan kategori hotel sesuai budget dan selera",
  "Bebas atur tempo, jam istirahat, dan minat khusus",
  "Halal food & muslim friendly arrangement",
  "Local support selama perjalanan",
];

export default function PrivateTrip() {
  const { openLead } = useLead();

  return (
    <>
      <SEO
        title="Private Trip Internasional — Liburan Sesuai Gaya Anda | Beyond Travel"
        description="Private trip untuk family, couple, honeymoon, friends, dan small group. Itinerary pribadi, transportasi privat, dan local support. Plan My Private Trip."
      />
      <PageHero
        image={HERO_IMG}
        eyebrow="Private Trip"
        title="Perjalanan Privat, Tempomu Aturansendiri"
        sub="Nikmati perjalanan eksklusif bersama orang-orang terdekat — tanpa rombongan, tanpa jadwal kaku, dengan layanan penuh dari tim kami."
        crumb="Private Trip"
      >
        <Link to="/custom-trip" className="btn btn-gold !px-8 !py-3.5" data-testid="private-trip-plan-cta">
          Plan My Private Trip <ArrowRight className="h-4 w-4" />
        </Link>
        <button
          onClick={() => {
            track("click_whatsapp", { source: "private_trip_hero" });
            openLead();
          }}
          className="btn btn-outline-white !px-8 !py-3.5"
          data-testid="private-trip-consult-cta"
        >
          Konsultasi via WhatsApp
        </button>
      </PageHero>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading
            eyebrow="Trip Styles"
            title="Private Trip Untuk Semua Gaya Perjalanan"
            sub="Pilih gaya perjalanan Anda, tim kami menyusun sisanya."
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TYPES.map((t, i) => (
              <Reveal key={t.title} delay={Math.min(i * 0.07, 0.3)} className="h-full">
                <div className="group h-full rounded-3xl border border-slate-100 bg-cream p-6 card-hover" data-testid={`private-type-${t.title.toLowerCase().replace(/ /g, "-")}`}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-tosca-600 text-gold transition-transform duration-300 group-hover:scale-110">
                    <t.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-charcoal">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{t.desc}</p>
                  <Link to="/custom-trip" className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-tosca-600 transition hover:text-gold-dark">
                    Mulai Rencanakan <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Why Private" title="Kenapa Private Trip Lebih Nyaman?" />
            <ul className="mt-8 space-y-4">
              {BENEFITS.map((b, i) => (
                <Reveal key={b} delay={Math.min(i * 0.07, 0.3)}>
                  <li className="flex items-start gap-3 rounded-2xl bg-white p-4 text-sm text-slate-600 shadow-crystal">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-tosca-600 text-white">
                      <Check className="h-3 w-3" />
                    </span>
                    {b}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://images.unsplash.com/photo-1599173705513-0880f530cd3d?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1200"
                alt="Private trip destinations"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <p className="font-heading text-lg font-bold text-white">Keluarga kecil, momen besar.</p>
                <p className="mt-1 text-sm text-white/70">Private trip memberi ruang dan waktu untuk orang yang paling berarti.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title="Plan My Private Trip"
        sub="Ceritakan rencana Anda — destinasi, tanggal, dan gaya perjalanan. Kami susun proposalnya."
      />
    </>
  );
}
